import { useCallback, useEffect, useState } from "react";

/**
 * useFullscreen — gerencia a Fullscreen API do navegador.
 *
 * Fluxo:
 *  - enter(): chama document.documentElement.requestFullscreen()
 *    (browsers exigem ação do usuário para disparar)
 *  - exit(): chama document.exitFullscreen()
 *  - toggle(): alterna entre os dois
 *  - isFullscreen: estado reativo (sincronizado com 'fullscreenchange' do DOM)
 *
 * Também escuta a tecla ESC (alguns browsers já saem nativamente, mas
 * mantemos o handler para limpar nosso state local de forma consistente).
 */
export default function useFullscreen() {
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const onChange = () => {
            const fsElement =
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.msFullscreenElement;
            setIsFullscreen(Boolean(fsElement));
        };
        const events = ["fullscreenchange", "webkitfullscreenchange", "msfullscreenchange"];
        events.forEach((ev) => document.addEventListener(ev, onChange));
        return () => events.forEach((ev) => document.removeEventListener(ev, onChange));
    }, []);

    const enter = useCallback(async () => {
        const el = document.documentElement;
        try {
            if (el.requestFullscreen) await el.requestFullscreen();
            else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
            else if (el.msRequestFullscreen) await el.msRequestFullscreen();
        } catch (e) {
            console.warn("Fullscreen request blocked:", e);
            throw e;
        }
    }, []);

    const exit = useCallback(async () => {
        try {
            if (document.exitFullscreen) await document.exitFullscreen();
            else if (document.webkitExitFullscreen) await document.webkitExitFullscreen();
            else if (document.msExitFullscreen) await document.msExitFullscreen();
        } catch (e) {
            console.warn("Exit fullscreen failed:", e);
        }
    }, []);

    const toggle = useCallback(() => {
        if (isFullscreen) return exit();
        return enter();
    }, [isFullscreen, enter, exit]);

    return { isFullscreen, enter, exit, toggle };
}
